import { endOfMonth, format, isSameMonth, startOfMonth } from "date-fns";

import { supabase } from "~/integrations/supabase/client";
import { fetchAllFacturas } from "~/services/salesRequestDocumentService";

interface MonthlySnapshot {
  id?: string;
  year: number;
  month: number;
  period_label: string;
  total_facturas_count: number;
  total_facturas_amount: number;
  total_credit_notes_count: number;
  total_credit_notes_amount: number;
  net_amount: number;
  cross_period_adjustments: number;
  is_locked: boolean;
  locked_at?: string;
  locked_by?: string;
  snapshot_created_at?: string;
  snapshot_created_by?: string;
  last_updated_at?: string;
  last_updated_by?: string;
  facturas_ids?: string[];
  credit_notes_ids?: string[];
  snapshot_hash?: string;
  details?: any;
}

/**
 * Service for managing monthly facturas snapshots
 * Provides immutable monthly totals to prevent historical data changes
 */
export class MonthlySnapshotService {
  /**
   * Get snapshot for a specific month
   */
  static async getSnapshot(
    year: number,
    month: number
  ): Promise<MonthlySnapshot | null> {
    try {
      const { data, error } = await supabase
        .from("monthly_facturas_snapshots")
        .select("*")
        .eq("year", year)
        .eq("month", month)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          // No snapshot found
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error("Error fetching monthly snapshot:", error);
      throw error;
    }
  }

  /**
   * Check if a snapshot exists for a given date
   */
  static async snapshotExists(date: Date): Promise<boolean> {
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // JS months are 0-indexed

    const snapshot = await this.getSnapshot(year, month);
    return snapshot !== null;
  }

  /**
   * Check if a month is locked (immutable)
   */
  static async isMonthLocked(date: Date): Promise<boolean> {
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // JS months are 0-indexed

    const snapshot = await this.getSnapshot(year, month);
    return snapshot?.is_locked || false;
  }

  /**
   * Create or update a monthly snapshot
   * Only admins can perform this operation
   */
  static async createOrUpdateSnapshot(
    year: number,
    month: number,
    forceUpdate: boolean = false
  ): Promise<MonthlySnapshot> {
    try {
      // Check if snapshot already exists
      const existingSnapshot = await this.getSnapshot(year, month);

      if (existingSnapshot?.is_locked && !forceUpdate) {
        throw new Error(
          `Month ${year}-${month.toString().padStart(2, "0")} is locked and cannot be updated`
        );
      }

      // Calculate date range for the month
      const startDate = startOfMonth(new Date(year, month - 1, 1));
      const endDate = endOfMonth(new Date(year, month - 1, 1));

      console.log(
        `📊 Creating snapshot for ${year}-${month.toString().padStart(2, "0")}`
      );
      console.log(
        `📅 Date range: ${format(startDate, "yyyy-MM-dd")} to ${format(endDate, "yyyy-MM-dd")}`
      );

      // Fetch all facturas for the month
      const allDocuments = await fetchAllFacturas(startDate, endDate);

      // Separate facturas and credit notes
      const facturas = allDocuments.filter(
        (doc) => doc.documentType === "factura"
      );
      const creditNotes = allDocuments.filter(
        (doc) => doc.documentType === "nota_credito"
      );

      console.log(
        `📋 Found ${facturas.length} facturas and ${creditNotes.length} credit notes`
      );

      // Calculate totals
      const totalFacturasAmount = facturas.reduce((sum, factura) => {
        const amount =
          (factura as any).price_with_iva || (factura as any).total_amount || 0;
        return sum + amount;
      }, 0);

      const totalCreditNotesAmount = creditNotes.reduce((sum, creditNote) => {
        const amount = Math.abs(
          (creditNote as any).price_with_iva ||
            (creditNote as any).total_amount ||
            0
        );
        return sum + amount;
      }, 0);

      const netAmount = totalFacturasAmount - totalCreditNotesAmount;

      // TODO: Calculate cross-period adjustments (credit notes from future months affecting this month)
      const crossPeriodAdjustments = 0;

      // Extract IDs for auditing
      const facturasIds = facturas.map((f) => f.id);
      const creditNotesIds = creditNotes.map((cn) => cn.id);

      // Create data hash for integrity (browser-compatible)
      const dataToHash = {
        year,
        month,
        totalFacturasAmount,
        totalCreditNotesAmount,
        netAmount,
        facturasIds: facturasIds.sort(),
        creditNotesIds: creditNotesIds.sort(),
      };

      // Simple hash function for browser compatibility
      const simpleHash = (str: string) => {
        let hash = 0;
        if (str.length === 0) return hash.toString();
        for (let i = 0; i < str.length; i++) {
          const char = str.charCodeAt(i);
          hash = (hash << 5) - hash + char;
          hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash).toString(16);
      };

      const snapshotHash = simpleHash(JSON.stringify(dataToHash));

      const snapshotData: Partial<MonthlySnapshot> = {
        year,
        month,
        period_label: `${year}-${month.toString().padStart(2, "0")}`,
        total_facturas_count: facturas.length,
        total_facturas_amount: Math.round(totalFacturasAmount),
        total_credit_notes_count: creditNotes.length,
        total_credit_notes_amount: Math.round(totalCreditNotesAmount),
        net_amount: Math.round(netAmount),
        cross_period_adjustments: Math.round(crossPeriodAdjustments),
        facturas_ids: facturasIds,
        credit_notes_ids: creditNotesIds,
        snapshot_hash: snapshotHash,
        details: {
          created_from_documents: allDocuments.length,
          date_range: {
            start: format(startDate, "yyyy-MM-dd"),
            end: format(endDate, "yyyy-MM-dd"),
          },
          calculation_notes: "Calculated from fetchAllFacturas service",
        },
      };

      let result;
      if (existingSnapshot) {
        // Update existing snapshot
        const { data, error } = await supabase
          .from("monthly_facturas_snapshots")
          .update(snapshotData)
          .eq("id", existingSnapshot.id)
          .select()
          .single();

        if (error) throw error;
        result = data;
        console.log(
          `✅ Updated snapshot for ${year}-${month.toString().padStart(2, "0")}`
        );
      } else {
        // Create new snapshot
        const { data, error } = await supabase
          .from("monthly_facturas_snapshots")
          .insert(snapshotData)
          .select()
          .single();

        if (error) throw error;
        result = data;
        console.log(
          `✅ Created snapshot for ${year}-${month.toString().padStart(2, "0")}`
        );
      }

      console.log(`📊 Snapshot totals:`, {
        facturas: `${facturas.length} documents = $${Math.round(totalFacturasAmount).toLocaleString()}`,
        creditNotes: `${creditNotes.length} documents = $${Math.round(totalCreditNotesAmount).toLocaleString()}`,
        netAmount: `$${Math.round(netAmount).toLocaleString()}`,
        isLocked: result.is_locked,
      });

      return result;
    } catch (error) {
      console.error("Error creating/updating monthly snapshot:", error);
      throw error;
    }
  }

  /**
   * Lock a monthly snapshot to make it immutable
   * Only admins can perform this operation
   */
  static async lockMonth(
    year: number,
    month: number
  ): Promise<MonthlySnapshot> {
    try {
      const existingSnapshot = await this.getSnapshot(year, month);

      if (!existingSnapshot) {
        throw new Error(
          `No snapshot found for ${year}-${month.toString().padStart(2, "0")}`
        );
      }

      if (existingSnapshot.is_locked) {
        throw new Error(
          `Month ${year}-${month.toString().padStart(2, "0")} is already locked`
        );
      }

      const { data, error } = await supabase
        .from("monthly_facturas_snapshots")
        .update({
          is_locked: true,
          locked_at: new Date().toISOString(),
        })
        .eq("id", existingSnapshot.id)
        .select()
        .single();

      if (error) throw error;

      console.log(
        `🔒 Locked month ${year}-${month.toString().padStart(2, "0")}`
      );
      return data;
    } catch (error) {
      console.error("Error locking monthly snapshot:", error);
      throw error;
    }
  }

  /**
   * Unlock a monthly snapshot (admin emergency function)
   * Only super admins should be able to perform this operation
   */
  static async unlockMonth(
    year: number,
    month: number
  ): Promise<MonthlySnapshot> {
    try {
      const existingSnapshot = await this.getSnapshot(year, month);

      if (!existingSnapshot) {
        throw new Error(
          `No snapshot found for ${year}-${month.toString().padStart(2, "0")}`
        );
      }

      if (!existingSnapshot.is_locked) {
        throw new Error(
          `Month ${year}-${month.toString().padStart(2, "0")} is not locked`
        );
      }

      const { data, error } = await supabase
        .from("monthly_facturas_snapshots")
        .update({
          is_locked: false,
          locked_at: null,
          locked_by: null,
        })
        .eq("id", existingSnapshot.id)
        .select()
        .single();

      if (error) throw error;

      console.log(
        `🔓 Unlocked month ${year}-${month.toString().padStart(2, "0")}`
      );
      return data;
    } catch (error) {
      console.error("Error unlocking monthly snapshot:", error);
      throw error;
    }
  }

  /**
   * Get the appropriate total for a date range
   * Uses snapshot data for locked months, live calculation for current/future months
   */
  static async getTotalForDateRange(
    startDate: Date,
    endDate: Date,
    userRole?: string,
    userId?: string
  ): Promise<{
    totalAmount: number;
    isFromSnapshot: boolean;
    snapshotData?: MonthlySnapshot;
    details: any;
  }> {
    try {
      // Check if this is a single complete month
      const isSingleMonth = isSameMonth(startDate, endDate);
      const isCompleteMonth =
        isSingleMonth &&
        startDate.getDate() === 1 &&
        endDate.getDate() ===
          new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();

      if (isCompleteMonth) {
        const year = startDate.getFullYear();
        const month = startDate.getMonth() + 1;
        const snapshot = await this.getSnapshot(year, month);

        if (snapshot && snapshot.is_locked) {
          console.log(
            `📋 Using locked snapshot for ${year}-${month.toString().padStart(2, "0")}`
          );
          return {
            totalAmount: snapshot.net_amount,
            isFromSnapshot: true,
            snapshotData: snapshot,
            details: {
              source: "locked_snapshot",
              period: snapshot.period_label,
              facturas: `${snapshot.total_facturas_count} documents = $${snapshot.total_facturas_amount.toLocaleString()}`,
              creditNotes: `${snapshot.total_credit_notes_count} documents = $${snapshot.total_credit_notes_amount.toLocaleString()}`,
              lockDate: snapshot.locked_at,
            },
          };
        }
      }

      // Fallback to live calculation
      console.log(
        `📊 Using live calculation for date range ${format(startDate, "yyyy-MM-dd")} to ${format(endDate, "yyyy-MM-dd")}`
      );
      const documents = await fetchAllFacturas(
        startDate,
        endDate,
        userRole,
        userId
      );

      const facturas = documents.filter(
        (doc) => doc.documentType === "factura"
      );
      const creditNotes = documents.filter(
        (doc) => doc.documentType === "nota_credito"
      );

      const totalFacturasAmount = facturas.reduce((sum, factura) => {
        const amount =
          (factura as any).price_with_iva || (factura as any).total_amount || 0;
        return sum + amount;
      }, 0);

      const totalCreditNotesAmount = creditNotes.reduce((sum, creditNote) => {
        const amount = Math.abs(
          (creditNote as any).price_with_iva ||
            (creditNote as any).total_amount ||
            0
        );
        return sum + amount;
      }, 0);

      const netAmount = totalFacturasAmount - totalCreditNotesAmount;

      return {
        totalAmount: Math.round(netAmount),
        isFromSnapshot: false,
        details: {
          source: "live_calculation",
          facturas: `${facturas.length} documents = $${Math.round(totalFacturasAmount).toLocaleString()}`,
          creditNotes: `${creditNotes.length} documents = $${Math.round(totalCreditNotesAmount).toLocaleString()}`,
          total_documents: documents.length,
        },
      };
    } catch (error) {
      console.error("Error getting total for date range:", error);
      throw error;
    }
  }

  /**
   * Create snapshots for all missing months up to the previous month
   */
  static async createMissingSnapshots(): Promise<MonthlySnapshot[]> {
    try {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1; // JS months are 0-indexed

      // Get all existing snapshots
      const { data: existingSnapshots, error } = await supabase
        .from("monthly_facturas_snapshots")
        .select("year, month");

      if (error) throw error;

      const existingKeys = new Set(
        existingSnapshots?.map((s) => `${s.year}-${s.month}`) || []
      );

      const createdSnapshots: MonthlySnapshot[] = [];

      // Create snapshots for missing months (go back 2 years)
      const startYear = currentYear - 2;

      for (let year = startYear; year <= currentYear; year++) {
        const startMonth = year === startYear ? 1 : 1;
        const endMonth = year === currentYear ? currentMonth - 1 : 12; // Don't include current month

        for (let month = startMonth; month <= endMonth; month++) {
          const key = `${year}-${month}`;

          if (!existingKeys.has(key)) {
            console.log(
              `📊 Creating missing snapshot for ${year}-${month.toString().padStart(2, "0")}`
            );
            try {
              const snapshot = await this.createOrUpdateSnapshot(year, month);
              createdSnapshots.push(snapshot);
            } catch (error) {
              console.error(`❌ Failed to create snapshot for ${key}:`, error);
            }
          }
        }
      }

      console.log(`✅ Created ${createdSnapshots.length} missing snapshots`);
      return createdSnapshots;
    } catch (error) {
      console.error("Error creating missing snapshots:", error);
      throw error;
    }
  }

  /**
   * Get all snapshots (for admin view)
   */
  static async getAllSnapshots(): Promise<MonthlySnapshot[]> {
    try {
      const { data, error } = await supabase
        .from("monthly_facturas_snapshots")
        .select("*")
        .order("year", { ascending: false })
        .order("month", { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error("Error fetching all snapshots:", error);
      throw error;
    }
  }

  /**
   * Create a snapshot with manual values
   * Only admins can perform this operation
   */
  static async createSnapshotManually(snapshotData: {
    year: number;
    month: number;
    period_label: string;
    total_facturas_count: number;
    total_facturas_amount: number;
    total_credit_notes_count: number;
    total_credit_notes_amount: number;
    net_amount: number;
    cross_period_adjustments: number;
    is_locked: boolean;
  }): Promise<MonthlySnapshot> {
    try {
      // Check if snapshot already exists
      const existingSnapshot = await this.getSnapshot(
        snapshotData.year,
        snapshotData.month
      );

      if (existingSnapshot) {
        throw new Error(
          `Snapshot for ${snapshotData.period_label} already exists`
        );
      }

      // Create hash for data integrity
      const dataToHash = {
        year: snapshotData.year,
        month: snapshotData.month,
        total_facturas_count: snapshotData.total_facturas_count,
        total_facturas_amount: snapshotData.total_facturas_amount,
        total_credit_notes_count: snapshotData.total_credit_notes_count,
        total_credit_notes_amount: snapshotData.total_credit_notes_amount,
        net_amount: snapshotData.net_amount,
        cross_period_adjustments: snapshotData.cross_period_adjustments,
      };

      const simpleHash = (str: string) => {
        let hash = 0;
        if (str.length === 0) return hash.toString();
        for (let i = 0; i < str.length; i++) {
          const char = str.charCodeAt(i);
          hash = (hash << 5) - hash + char;
          hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash).toString(16);
      };

      const snapshotHash = simpleHash(JSON.stringify(dataToHash));

      const { data, error } = await supabase
        .from("monthly_facturas_snapshots")
        .insert({
          year: snapshotData.year,
          month: snapshotData.month,
          period_label: snapshotData.period_label,
          total_facturas_count: snapshotData.total_facturas_count,
          total_facturas_amount: snapshotData.total_facturas_amount,
          total_credit_notes_count: snapshotData.total_credit_notes_count,
          total_credit_notes_amount: snapshotData.total_credit_notes_amount,
          net_amount: snapshotData.net_amount,
          cross_period_adjustments: snapshotData.cross_period_adjustments,
          is_locked: snapshotData.is_locked,
          snapshot_hash: snapshotHash,
          facturas_ids: [],
          credit_notes_ids: [],
          details: {
            created_manually: true,
            created_at: new Date().toISOString(),
          },
        })
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Error creating manual snapshot:", error);
      throw error;
    }
  }

  /**
   * Update a snapshot with manual values
   * Only admins can perform this operation
   */
  static async updateSnapshotManually(
    snapshotId: string,
    snapshotData: {
      year: number;
      month: number;
      period_label: string;
      total_facturas_count: number;
      total_facturas_amount: number;
      total_credit_notes_count: number;
      total_credit_notes_amount: number;
      net_amount: number;
      cross_period_adjustments: number;
      is_locked: boolean;
    }
  ): Promise<MonthlySnapshot> {
    try {
      // Check if snapshot exists and is not locked
      const existingSnapshot = await this.getSnapshot(
        snapshotData.year,
        snapshotData.month
      );

      if (!existingSnapshot) {
        throw new Error(
          `Snapshot for ${snapshotData.period_label} does not exist`
        );
      }

      if (existingSnapshot.is_locked) {
        throw new Error(
          `Cannot update locked snapshot for ${snapshotData.period_label}`
        );
      }

      // Create hash for data integrity
      const dataToHash = {
        year: snapshotData.year,
        month: snapshotData.month,
        total_facturas_count: snapshotData.total_facturas_count,
        total_facturas_amount: snapshotData.total_facturas_amount,
        total_credit_notes_count: snapshotData.total_credit_notes_count,
        total_credit_notes_amount: snapshotData.total_credit_notes_amount,
        net_amount: snapshotData.net_amount,
        cross_period_adjustments: snapshotData.cross_period_adjustments,
      };

      const simpleHash = (str: string) => {
        let hash = 0;
        if (str.length === 0) return hash.toString();
        for (let i = 0; i < str.length; i++) {
          const char = str.charCodeAt(i);
          hash = (hash << 5) - hash + char;
          hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash).toString(16);
      };

      const snapshotHash = simpleHash(JSON.stringify(dataToHash));

      const { data, error } = await supabase
        .from("monthly_facturas_snapshots")
        .update({
          year: snapshotData.year,
          month: snapshotData.month,
          period_label: snapshotData.period_label,
          total_facturas_count: snapshotData.total_facturas_count,
          total_facturas_amount: snapshotData.total_facturas_amount,
          total_credit_notes_count: snapshotData.total_credit_notes_count,
          total_credit_notes_amount: snapshotData.total_credit_notes_amount,
          net_amount: snapshotData.net_amount,
          cross_period_adjustments: snapshotData.cross_period_adjustments,
          is_locked: snapshotData.is_locked,
          snapshot_hash: snapshotHash,
          last_updated_at: new Date().toISOString(),
          details: {
            updated_manually: true,
            updated_at: new Date().toISOString(),
          },
        })
        .eq("id", snapshotId)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Error updating manual snapshot:", error);
      throw error;
    }
  }
}
